/**
 * EmptyDashboard — first-run state when no Members have been hired yet.
 * Greeting + Getting-Started cards + 4 empty StatTiles +
 * StartingMembersGrid + HowItWorksStrip.
 */

import { useMemo } from 'react';
import { usePositionsCatalog, useStaffProfiles, useUnitsCatalog } from '@/features/members/hooks';
import { t } from '@/lib/i18n';
import { DashboardGreeting } from '../components/DashboardGreeting';
import { StatTile } from '../components/StatTile';
import { GetStartedCards } from '../components/GetStartedCards';
import { StartingMembersGrid } from '../components/StartingMembersGrid';
import { HowItWorksStrip } from '../components/HowItWorksStrip';
import styles from './EmptyDashboard.module.css';

export interface EmptyDashboardProps {
  firstName: string;
}

export function EmptyDashboard({ firstName }: EmptyDashboardProps) {
  const staffProfiles = useStaffProfiles();
  const positions = usePositionsCatalog();
  const units = useUnitsCatalog();
  const profiles = staffProfiles.data?.items ?? [];
  const positionsById = useMemo(
    () => new Map((positions.data?.items ?? []).map((p) => [p.id, p.name])),
    [positions.data?.items],
  );
  const unitsById = useMemo(
    () => new Map((units.data?.items ?? []).map((u) => [u.id, u.name])),
    [units.data?.items],
  );

  return (
    <div className={styles.root} data-testid="dashboard-state-empty">
      <DashboardGreeting state="empty" firstName={firstName} />
      <GetStartedCards state="empty" />
      <section className={styles.metricsSection}>
        <h2 className={styles.metricsTitle}>{t('Current Activity')}</h2>
        <div className={styles.tileRow}>
          <StatTile label={t('Team Members')} value="—" subtitle={t('nothing yet')} empty />
          <StatTile label={t('Jobs Running')} value="—" subtitle={t('nothing yet')} empty />
          <StatTile label={t('Need Your Input')} value="—" subtitle={t('nothing yet')} empty />
          <StatTile label={t('Jobs Completed')} value="—" subtitle={t('nothing yet')} empty />
        </div>
      </section>
      <StartingMembersGrid
        profiles={profiles}
        positionsById={positionsById}
        unitsById={unitsById}
      />
      <HowItWorksStrip />
    </div>
  );
}
